import {enabledFeatureLabels} from './enabledFeatures.js'

// Shown when a save, archive or load fails with nothing a user can act on
// (an HTML error page, a stack dump, or no body at all).
export const GENERIC_SAVE_ERROR = 'Something went wrong saving this group report. If this keeps happening, please contact support.'

// Messages longer than this, or that look like markup, are internals.
const MAX_MESSAGE_LENGTH = 160

const FREQUENCY_ORDER = ['daily', 'daily-data', 'weekly', 'semi-monthly', 'monthly']

// A weekly report saved without a day is sent on Monday (BaseReport default).
const DEFAULT_WEEKDAY = 1

/**
 * A group report as the page uses it, from a report-templates API row.
 * `groups` is the API's map of group id to {GroupName, can_view}.
 */
export function toTemplate(row, groups = {}) {
    const settings = row.report_settings ?? {}

    return {
        id: row.id,
        groupId: row.group_id,
        groupName: row.group_name ?? groups[row.group_id]?.GroupName ?? '',
        name: row.template_name,
        interval: row.report_duration_type,
        weekday: settings.weekday ?? null,
        settings,
        canView: row.can_view !== false,
    }
}

const SORT_VALUES = {
    name: (template) => template.name.toLowerCase(),
    group: (template) => template.groupName.toLowerCase(),
    frequency: (template) => FREQUENCY_ORDER.indexOf(template.interval),
    includes: (template, schema) => enabledFeatureLabels(schema, template.settings).length,
}

const compare = (a, b) => (a < b ? -1 : (a > b ? 1 : 0))

/**
 * Group reports in table order. With no column picked: by group, then name.
 * A picked column sorts by that value (name breaks ties) in `direction`.
 * Returns a new array.
 */
export function sortTemplates(templates, schema, column, direction = 'asc') {
    const byName = (a, b) => compare(SORT_VALUES.name(a), SORT_VALUES.name(b))

    if (! column) {
        return [...templates].sort((a, b) => compare(SORT_VALUES.group(a), SORT_VALUES.group(b)) || byName(a, b))
    }

    const value = SORT_VALUES[column]
    const sign = direction === 'desc' ? -1 : 1

    return [...templates].sort((a, b) => (sign * compare(value(a, schema), value(b, schema))) || byName(a, b))
}

const presentable = (message) => (
    typeof message === 'string'
    && message.trim() !== ''
    && message.length <= MAX_MESSAGE_LENGTH
    && ! /^\s*[<{]/.test(message)
)

/**
 * Messages to show for a failed request: validation messages and short plain
 * replies as written; anything else as one generic line.
 */
export function saveErrorMessages(error) {
    const data = error?.response?.data
    let messages = []

    if (typeof data === 'string') {
        messages = [data]
    } else if (data?.errors && typeof data.errors === 'object') {
        messages = Object.values(data.errors).flat()
    } else if (typeof data?.message === 'string') {
        messages = [data.message]
    }

    messages = messages.filter(presentable)

    return messages.length > 0 ? messages : [GENERIC_SAVE_ERROR]
}

/**
 * The settings an Edit page starts from: the group report's own, never the
 * site's defaults (those would show values it does not send with, and a save
 * would write them in). A weekly report with no day shows the day it is
 * actually sent on.
 */
export function editableTemplateSettings(template) {
    const settings = JSON.parse(JSON.stringify(template.settings ?? {}))

    if (template.interval === 'weekly' && (settings.weekday === null || settings.weekday === undefined)) {
        settings.weekday = DEFAULT_WEEKDAY
    }

    return settings
}

const canonical = (value) => {
    if (Array.isArray(value)) {
        return value.map(canonical)
    }
    if (value !== null && typeof value === 'object') {
        return Object.fromEntries(Object.keys(value).sort().map((key) => [key, canonical(value[key])]))
    }
    // Selects hand values back as strings (a stored weekday 3 comes back as
    // "3"), so numbers and numeric strings count as the same value.
    return typeof value === 'number' ? String(value) : value
}

/**
 * A comparable snapshot of a group report form, for telling whether the user
 * changed anything. Ignores key order and number-vs-string differences.
 */
export function formFingerprint(form) {
    return JSON.stringify(canonical(form))
}
